/**
 * Depth Anything V2 — single-frame monocular depth estimator.
 *
 * Used at recording-Stop time on a single representative frame to
 * recover an *up-to-scale* depth map. Combined with a 2D pose, this
 * lets us estimate body height in metres, floor plane, and camera
 * height — feeding the SubjectProfile / SceneCalibration so downstream
 * metrics can render in absolute units.
 *
 * Tier:
 *   - PC     → Depth Anything V2 *base*  (~95 MB, ~50 ms WebGPU)
 *   - Mobile → Depth Anything V2 *small* (~25 MB, ~80 ms wasm)
 *
 * docs/migration-plan.md Phase 13.
 */

import * as ort from 'onnxruntime-web';

import { ensureOrtWasmEnv, fetchModelCached, pickOnnxEPs } from '../onnx-runtime';

export type DepthAnythingTier = 'small' | 'base';

export interface DepthAnythingOptions {
  tier: DepthAnythingTier;
  /** Override URL for self-hosting. Default:
   *  /models/depth-anything-v2-{tier}.onnx */
  modelUrl?: string;
}

export interface DepthEstimateResult {
  /** Depth map in row-major order, length = width × height. Values are
   *  *relative* (up-to-scale); call code is expected to anchor scale
   *  via a known landmark distance (e.g. height of a calibrated
   *  subject) before producing absolute metres. */
  data: Float32Array;
  width: number;
  height: number;
  tier: DepthAnythingTier;
}

const DEFAULT_INPUT_DIMS: Readonly<Record<DepthAnythingTier, { width: number; height: number }>> = {
  // Depth Anything V2 was trained at 518×518 (multiples of 14). The
  // ONNX exports ship with that fixed input shape; the host MUST
  // letterbox-resize the source frame before invoking estimate().
  small: { width: 518, height: 518 },
  base: { width: 518, height: 518 },
};

const DEFAULT_MODEL_URLS: Readonly<Record<DepthAnythingTier, string>> = {
  small: '/models/depth-anything-v2-small.onnx',
  base: '/models/depth-anything-v2-base.onnx',
};

export class DepthAnythingV2 {
  readonly id: string;
  readonly tier: DepthAnythingTier;
  private session: ort.InferenceSession | null = null;
  private inputName: string | null = null;
  private outputName: string | null = null;
  private cropCanvas: HTMLCanvasElement | null = null;
  private readonly inputWidth: number;
  private readonly inputHeight: number;

  constructor(private readonly opts: DepthAnythingOptions) {
    this.tier = opts.tier;
    this.id = `depth-anything-v2-${opts.tier}`;
    const dims = DEFAULT_INPUT_DIMS[opts.tier];
    this.inputWidth = dims.width;
    this.inputHeight = dims.height;
  }

  async initialize(): Promise<void> {
    if (this.session) return;
    ensureOrtWasmEnv();
    const url = this.opts.modelUrl ?? DEFAULT_MODEL_URLS[this.opts.tier];
    // base tier benefits massively from WebGPU; small stays on wasm
    // for predictable thermal behaviour on phones.
    const { list } = pickOnnxEPs({ preferWebGpu: this.opts.tier === 'base' });
    const { bytes, diag } = await fetchModelCached(url);
    console.info(
      '[depth-anything-v2]',
      this.opts.tier,
      diag.source,
      `${(diag.byteLength / 1e6).toFixed(1)}MB`,
      `${diag.ms.toFixed(0)}ms`,
    );
    this.session = await ort.InferenceSession.create(bytes, {
      executionProviders: list,
      graphOptimizationLevel: 'all',
    });
    this.inputName = this.session.inputNames[0];
    this.outputName = this.session.outputNames[0];
    this.cropCanvas = document.createElement('canvas');
    this.cropCanvas.width = this.inputWidth;
    this.cropCanvas.height = this.inputHeight;
  }

  async estimate(source: CanvasImageSource): Promise<DepthEstimateResult | null> {
    if (!this.session || !this.inputName || !this.outputName || !this.cropCanvas) return null;
    const ctx = this.cropCanvas.getContext('2d');
    if (!ctx) return null;
    ctx.clearRect(0, 0, this.inputWidth, this.inputHeight);
    ctx.drawImage(source, 0, 0, this.inputWidth, this.inputHeight);
    const image = ctx.getImageData(0, 0, this.inputWidth, this.inputHeight);
    const tensor = imageToCHW(image, this.inputWidth, this.inputHeight);
    const result = await this.session.run({ [this.inputName]: tensor });
    const out = result[this.outputName].data as Float32Array;
    if (!out) return null;
    return {
      data: new Float32Array(out),
      width: this.inputWidth,
      height: this.inputHeight,
      tier: this.tier,
    };
  }

  async dispose(): Promise<void> {
    try {
      await this.session?.release?.();
    } catch {
      /* ignore */
    }
    this.session = null;
    this.cropCanvas = null;
    this.inputName = null;
    this.outputName = null;
  }
}

function imageToCHW(image: ImageData, width: number, height: number): ort.Tensor {
  const { data } = image;
  const plane = width * height;
  const rgb = new Float32Array(3 * plane);
  // Depth Anything V2 uses ImageNet normalisation:
  // mean=[0.485,0.456,0.406] std=[0.229,0.224,0.225].
  const mean = [0.485, 0.456, 0.406];
  const std = [0.229, 0.224, 0.225];
  for (let i = 0; i < plane; i += 1) {
    const base = i * 4;
    rgb[i] = (data[base] / 255 - mean[0]) / std[0];
    rgb[i + plane] = (data[base + 1] / 255 - mean[1]) / std[1];
    rgb[i + 2 * plane] = (data[base + 2] / 255 - mean[2]) / std[2];
  }
  return new ort.Tensor('float32', rgb, [1, 3, height, width]);
}
